/**
 * Consent-region resolution for the region-aware consent checkbox.
 *
 * Regulated ("EU") regions must see the consent checkbox un-ticked by
 * default, with progression blocked until the user explicitly ticks it.
 * Everywhere else the checkbox may be pre-ticked. This module owns the
 * classification so the rule lives in exactly one place:
 *
 *   - `EXPLICIT_CONSENT_COUNTRIES` is the compliance-sensitive constant —
 *     adding or removing a jurisdiction is a one-line change here.
 *   - `resolveConsentRegion()` turns browser locale/timezone metadata into
 *     `'eu' | 'non-eu'`, biased toward `'eu'` whenever the signals are
 *     ambiguous or conflicting: an unnecessary un-ticked checkbox costs one
 *     tap, while a wrongly pre-ticked one is a compliance defect.
 *
 * Partners who know the user's jurisdiction can bypass detection entirely
 * via the `consent-region` attribute / `consent_region` embed config.
 */

export const CONSENT_REGIONS = Object.freeze({
  EU: 'eu',
  NON_EU: 'non-eu',
});

/**
 * ISO 3166-1 alpha-2 codes of jurisdictions that require an explicit,
 * un-ticked consent checkbox: the EU-27, the EEA (IS/LI/NO), plus the UK
 * and Switzerland, whose GDPR-equivalent regimes (UK GDPR, revFADP) carry
 * the same explicit-consent bar. Trimming or extending this list is a
 * compliance decision, not an engineering one.
 */
export const EXPLICIT_CONSENT_COUNTRIES = Object.freeze([
  // EU-27
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  // EEA
  'IS',
  'LI',
  'NO',
  // GDPR-equivalent regimes
  'GB',
  'CH',
]);

/**
 * Extract the region subtag from a BCP-47 language tag, using likely-subtag
 * maximisation so bare tags still resolve (e.g. `fr` → `FR`). Returns null
 * when the tag is malformed or carries no resolvable region.
 * @param {string} tag
 * @returns {string | null} upper-case ISO 3166-1 alpha-2 code or null
 */
const regionFromLocaleTag = (tag) => {
  try {
    const locale = new Intl.Locale(tag);
    const region = locale.region || locale.maximize().region;
    return region ? region.toUpperCase() : null;
  } catch {
    return null;
  }
};

/**
 * Resolve the consent region from the user's locale metadata.
 *
 * Classification rules, in order of precedence:
 *  1. Any locale tag whose region resolves to an explicit-consent country →
 *     `'eu'`.
 *  2. The IANA timezone falling under `Europe/` → `'eu'`. This is broader
 *     than the country list (e.g. `Europe/Istanbul`) — deliberately so:
 *     GDPR applies by the user's location, and over-asking is harmless.
 *  3. At least one signal resolved and none pointed at Europe → `'non-eu'`.
 *  4. Nothing resolved → `'eu'` (the compliance-safe default).
 *
 * @param {readonly string[]} [locales] BCP-47 tags, most-preferred first.
 *   Defaults to `navigator.languages` (falling back to `navigator.language`).
 * @param {string} [timeZone] IANA timezone. Defaults to the runtime's
 *   resolved timezone.
 * @returns {'eu' | 'non-eu'}
 */
export const resolveConsentRegion = (locales, timeZone) => {
  let resolvedLocales = locales;
  if (!resolvedLocales && typeof navigator !== 'undefined') {
    resolvedLocales =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language].filter(Boolean);
  }

  let resolvedTimeZone = timeZone;
  if (resolvedTimeZone === undefined) {
    try {
      resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      resolvedTimeZone = undefined;
    }
  }

  const localeRegions = (resolvedLocales || [])
    .map(regionFromLocaleTag)
    .filter(Boolean);

  if (
    localeRegions.some((region) => EXPLICIT_CONSENT_COUNTRIES.includes(region))
  ) {
    return CONSENT_REGIONS.EU;
  }

  let sawResolvableSignal = localeRegions.length > 0;

  if (typeof resolvedTimeZone === 'string' && resolvedTimeZone.length > 0) {
    sawResolvableSignal = true;
    if (resolvedTimeZone.startsWith('Europe/')) {
      return CONSENT_REGIONS.EU;
    }
  }

  return sawResolvableSignal ? CONSENT_REGIONS.NON_EU : CONSENT_REGIONS.EU;
};

/**
 * Normalise a `consent-region` attribute / config value. Anything other
 * than an explicit `'eu'` / `'non-eu'` (case-insensitive) means "detect".
 * @param {unknown} value
 * @returns {'eu' | 'non-eu' | null} null when detection should run
 */
export const normalizeConsentRegion = (value) => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === CONSENT_REGIONS.EU) return CONSENT_REGIONS.EU;
  if (normalized === CONSENT_REGIONS.NON_EU) return CONSENT_REGIONS.NON_EU;
  return null;
};
